// ---------------------------------------------------------------------------
// Dynamic SSO allowlist (SQLite-backed).
//
// Checks the Express server's /api/allowlist/check endpoint, which queries a
// SQLite database at runtime. Adding or removing users no longer requires a
// redeploy -- use the admin REST endpoints on the server instead.
//
// This is a client-side UX gate, not a hard security boundary -- it can be
// bypassed via devtools by anyone who can already complete Microsoft SSO.
// Real access to data is still governed by whatever Databricks/Graph/dbt
// Cloud permissions the signed-in account actually has. For a hard security
// boundary, restrict sign-in at the Azure AD app registration itself
// (Enterprise application "Assignment required" + assigned users/groups).
// ---------------------------------------------------------------------------

/**
 * Whether the given email/UPN is allowed to sign in. Queries the server-side
 * SQLite allowlist. Returns true when no allowlist is configured (empty
 * database table) or the email is present. Fails open on network errors so
 * a transient outage doesn't lock everyone out.
 */
export async function isAllowedSsoUser(email: string): Promise<boolean> {
  try {
    const res = await fetch(
      `/api/allowlist/check?email=${encodeURIComponent(email.trim())}`
    )
    if (!res.ok) return true // fail-open if endpoint is down
    const data: { allowed: boolean } = await res.json()
    return data.allowed
  } catch {
    return true // fail-open on network error
  }
}

// ---------------------------------------------------------------------------
// Admin allowlist management.
//
// These call the server's protected /api/allowlist endpoints, which require
// the PRISM_ADMIN_TOKEN as an `x-admin-token` header. The token is never
// stored in the app -- an admin enters it in the UI and it is passed through
// on each request for the session only.
// ---------------------------------------------------------------------------

export interface AllowlistUser {
  email: string
  added_by?: string | null
  added_at?: string | null
}

/**
 * Whether the given signed-in email is a Prism admin, based on the
 * comma-separated VITE_ADMIN_EMAILS build config. Used only to decide whether
 * to surface admin UI (e.g. the "Manage users" panel) -- it is NOT a security
 * boundary. The real boundary is the server's PRISM_ADMIN_TOKEN, which admin
 * API calls still require. Returns false when no admins are configured, so the
 * admin UI stays hidden by default.
 */
export function isAdminUser(email?: string | null): boolean {
  if (!email) return false
  const configured = (import.meta.env.VITE_ADMIN_EMAILS as string | undefined) ?? ''
  const admins = configured
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)
  if (admins.length === 0) return false
  return admins.includes(email.trim().toLowerCase())
}

function adminHeaders(token: string): Record<string, string> {
  return { 'x-admin-token': token }
}

async function adminErrorMessage(res: Response): Promise<string> {
  if (res.status === 403) return 'Invalid admin token.'
  if (res.status === 503) return 'Admin API is not configured on the server.'
  try {
    const data = (await res.json()) as { error?: string }
    return data.error ?? `Request failed (${res.status}).`
  } catch {
    return `Request failed (${res.status}).`
  }
}

/** List every allowed user. Requires a valid admin token. */
export async function listAllowedUsers(
  token: string
): Promise<AllowlistUser[]> {
  const res = await fetch('/api/allowlist', { headers: adminHeaders(token) })
  if (!res.ok) throw new Error(await adminErrorMessage(res))
  return (await res.json()) as AllowlistUser[]
}

/** Add a user to the allowlist. Requires a valid admin token. */
export async function addAllowedUser(
  token: string,
  email: string,
  addedBy?: string
): Promise<void> {
  const res = await fetch('/api/allowlist', {
    method: 'POST',
    headers: { ...adminHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim(), added_by: addedBy }),
  })
  if (!res.ok) throw new Error(await adminErrorMessage(res))
}

/**
 * Whether the current signed-in user is on the allowlist. Reads the flag
 * set during sign-in. Non-allowlisted users can still use the app but
 * only see sections that do not require Databricks workspace access.
 */
export function isAllowlisted(): boolean {
  return localStorage.getItem('prism_allowlisted') !== 'false'
}

/** Remove a user from the allowlist. Requires a valid admin token. */
export async function removeAllowedUser(
  token: string,
  email: string
): Promise<void> {
  const res = await fetch(
    `/api/allowlist/${encodeURIComponent(email.trim())}`,
    { method: 'DELETE', headers: adminHeaders(token) }
  )
  if (!res.ok) throw new Error(await adminErrorMessage(res))
}
